import { TemplatePrompts } from '@kimuno/api-sdk';
import { palette, spaces } from '@kimuno/theme';
import { Stack, Typography } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectUser } from '../../../../reducers/user.selectors';
import services from '../../../../services';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import { getAIAssistantPageSource } from '../../../../utils/getAIAssistantPageSource';
import { ActiveView } from '../../shared/constants';
import AIAssistantFooter from '../../shared/Footer/AIAssistantFooter';
import AIAssistantDialogContentWithHeadline from '../../shared/Layout/AIAssistantContentWithHeadline';
import AIAssistantTemplatePromptList from '../../shared/TemplatePromptForm/AIAssistantTemplatePromptList';
import useTemplatePromptGuards from '../../shared/useTemplatePromptGuards';
import { getTemplatePromptsByView } from '../../shared/utils';
import { AIAssistantPageSelector } from './AIAssistantPageSelector';

const AIAssistantUnitEntry = () => {
	const { t } = useTranslation();
	const { processNewConversation, isExtended } = useContext(aiAssistantContext);

	const user = useSelector(selectUser);
	const canOpenTemplate = useTemplatePromptGuards();
	const isLernposterEnabled = !user?.isSchoolUser;

	const allPromptItems = getTemplatePromptsByView(ActiveView.UNIT_ENTRY);
	const promptItems = isLernposterEnabled
		? allPromptItems
		: allPromptItems.filter((item) => item.id !== TemplatePrompts.CREATE_LEARNING_POSTER);

	const contentStyles = {
		position: 'relative',
		padding: 0,
	};

	const handleClick = (title: string, subheader: string, id: TemplatePrompts) => {
		if (!canOpenTemplate(id)) return;

		// No form opens here: the reader card sends the request straight away. The
		// open is still tracked so every template enters the same funnel (WF-304).
		services.track.ampliTrackAIAssistantTemplateOpenedEvent({
			promptTemplate: id,
			pageSource: getAIAssistantPageSource(window.location.pathname),
		});
		processNewConversation({
			message: `${title}: ${subheader}`,
			messageContext: { templatePromptId: id },
		});
	};

	return (
		<>
			<AIAssistantDialogContentWithHeadline
				headline={
					<Stack spacing={spaces.xs}>
						<Typography variant="subtitle2" sx={{ color: palette.text.secondary }}>
							{t('AI suggestions for this unit')}
						</Typography>
					</Stack>
				}
				sx={contentStyles}
				data-testid="ai-assistant-unit-entry"
			>
				<Stack sx={{ paddingX: spaces.md, paddingTop: spaces.md }}>
					<AIAssistantPageSelector />
					<AIAssistantTemplatePromptList
						items={promptItems}
						isExtended={isExtended}
						onPromptClick={handleClick}
					/>
				</Stack>
			</AIAssistantDialogContentWithHeadline>
			<AIAssistantFooter
				caption={
					<Typography variant="caption" sx={{ color: palette.text.secondary }}>
						{t('All suggestions are based on the page selection of your currently open document')}
					</Typography>
				}
			/>
		</>
	);
};

export default AIAssistantUnitEntry;
